import { ICartViewModel, PromoCodeMessageCategory } from '@/@types/client-api';
import { AnalyticsContext } from '@/analytics/constants';
import { trackApplyPromoCode } from '@/analytics/ecommerce';
import useToast from '@/hooks/useToast';
import { useApplyPromoCodeMutation } from '@/redux/api/client/cart';
import { useCallback, useState } from 'react';

const promoCodeInvalidMessage = 'Promo code is incorrect';
const promoCodeErrorMessage = 'There was a problem applying the promo code. Please try again.';
/**
 * Formats a promo code string to ensure it has a space after the 4th character.
 * ABCD_12345 -> ABCD 12345
 * ABCD12345 -> ABCD 12345
 * Note: Although technically SourceCode/KeyCode in WMS may have different length/format,
 * this is the only format that has been historically supported in PLIEDU Cart experience.
 */
export const formatPromoCode = (code: string): string => {
  if (!code || code.length < 5) return code;

  // Remove any existing spaces, underscores, or special characters
  const sanitized = code.replace(/[^a-zA-Z0-9]/g, '');

  // Insert a space after the 4th character
  return `${sanitized.slice(0, 4)} ${sanitized.slice(4)}`;
};

export const usePromoCode = () => {
  const [applyPromoCode, { isLoading }] = useApplyPromoCodeMutation();
  const [inputErrorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const { showSuccessMessage, showFailMessage } = useToast();

  const handleApplyPromoCode = useCallback(
    (promoCode: string, cart?: ICartViewModel, context?: AnalyticsContext) => {
      if (!promoCode?.length) return;

      setErrorMessage(undefined);
      // Format the promo code before applying it
      const formattedPromoCode = formatPromoCode(promoCode);
      const previousCartDiscount = cart?.discountValue ?? 0;

      applyPromoCode({ promoCode: formattedPromoCode })
        .then(response => {
          if (response?.data) {
            const messages = [
              ...new Set(
                response.data.resultMessages
                  ?.filter(m => m.category === PromoCodeMessageCategory._0)
                  .map(m => m.message)
              )
            ];
            const success = (messages?.length ?? 0) > 0;
            if (success) {
              messages?.forEach(msg => {
                showSuccessMessage(msg);
              });
            } else {
              const msg = response.data?.resultMessages?.find(
                msg => msg.category === PromoCodeMessageCategory._1
              );
              setErrorMessage(msg?.message ?? promoCodeInvalidMessage);
            }
            trackApplyPromoCode(
              response.data.cart,
              context,
              success,
              (response.data.cart?.discountValue ?? previousCartDiscount) - previousCartDiscount
            );
          }
          if (response?.error) {
            setErrorMessage(promoCodeInvalidMessage);
          }
        })
        .catch(() => {
          showFailMessage(promoCodeErrorMessage);
        });
    },
    [applyPromoCode, showFailMessage, showSuccessMessage]
  );

  return {
    handleApplyPromoCode,
    isLoading,
    inputErrorMessage
  };
};
